/**
 * **The registration flow's progress hairline** — 2px across the very top of the page, growing a
 * quarter per step.
 *
 * The D3 poster mockup's one ADDED use of the accent
 * (`docs/design/rejestracja-premium-2026-09-21/d3-plakat-dark-losowanie.html`, `.hair`): the border
 * runs the full width and the ember fill runs 25 / 50 / 75 / 100% of it. Since the owner took the
 * "Krok 1 z 2" indicator out of the form column (21.09.2026) it is the ONLY channel the progress
 * has. It exists only where there IS a flow — sign-up and `/complete-profile`. Sign-in, the
 * password reset and the refusal screen are one screen each and draw none.
 *
 * ── It is `aria-hidden`, and that is now a known gap ────────────────────────────────────────
 * It was decorative because the words beside it said the same thing; with the words gone, a
 * trainer using a screen reader hears no position in the flow at all. Promoting it is not a
 * silent fix: this bar counts FOUR beats (the role choice and the confirmation screen are steps of
 * the visit, not of the account) where the copy that was removed counted two, so giving it a role
 * means choosing which count the flow announces and writing a label for it. That is the owner's
 * call, not this component's, and it is open.
 *
 * ── Why it animates between two fractions instead of from zero ──────────────────────────────
 * The mockup transitions the fill's WIDTH from the fraction it was at to the fraction it is going
 * to, so the bar grows rather than being redrawn. Every auth step mounts its own `AuthShell` — the
 * steps are different component types, so React swaps rather than reuses — which means this
 * element is born again on each step with no idea where the bar stood a moment ago. The caller
 * remembers it (`AuthShell`, for the same reason and by the same mechanism `HeroPhoto` remembers
 * the last photograph painted) and hands both ends in, so this component decides nothing.
 *
 * `scaleX` on a full-width bar rather than `width`, because a transform is composited and a width
 * is a layout on every frame of a 620ms tween. The duration and the curve are the dictionary's
 * `growX` — the handoff's own 620ms on the screen ease — with its 220ms base delay dropped, which
 * the mockup does not have: the bar answers the press.
 */

import { motion } from 'motion/react';

import { screenMotion, useScreenMotionPreference } from '../design-system/motion';

import styles from './AuthProgress.module.css';

interface AuthProgressProps {
  /** Where the bar stood on the previous screen, 0 to 1. */
  readonly from: number;
  /** Where it stands on this one, 0 to 1. */
  readonly to: number;
}

export function AuthProgress({ from, to }: AuthProgressProps) {
  const reducedMotion = useScreenMotionPreference();
  const grow = screenMotion('growX', { reducedMotion, delay: 0 });

  return (
    <div className={styles.rail} aria-hidden="true">
      <motion.span
        className={styles.fill}
        initial={{ scaleX: from }}
        animate={{ scaleX: to }}
        transition={grow.transition}
      />
    </div>
  );
}
